import React, { useState, useEffect } from 'react';
import { Cell, Grid } from 'react-md';
import ApiRequestService from '../component/ApiRequestService';

const EXPECT_ALL_KEY = '__expect_all__';

const ExcludeAOAnswerOptions = ({ subcategoryMetadata = [], selectedSubcategoryKeys = [] }) => {
    const [subcategoryNames, setSubcategoryNames] = useState([]);
    const [checkedKeys, setCheckedKeys] = useState(new Set());

    const keysStr = [...(selectedSubcategoryKeys || [])].sort().join(',');

    useEffect(() => {
        if (subcategoryMetadata && subcategoryMetadata.length > 0) {
            const names = subcategoryMetadata
                .filter(m => m.subcategory_name)
                .map(m => ({ key: m.key, name: m.subcategory_name }));
            if (names.length > 0) {
                setSubcategoryNames(names);
                return;
            }
        }

        if (!keysStr) { setSubcategoryNames([]); return; }

        const parsed = keysStr.split(',').map(key => {
            const [subject_id, topic_id, category_id, subcategory_id] = key.split('_').map(Number);
            return { key, subject_id, topic_id, category_id, subcategory_id };
        });

        const byCategory = {};
        parsed.forEach(p => {
            const ck = `${p.subject_id}_${p.topic_id}_${p.category_id}`;
            if (!byCategory[ck]) byCategory[ck] = { ...p, items: [] };
            byCategory[ck].items.push(p);
        });

        Promise.all(
            Object.values(byCategory).map(async (group) => {
                const subRes = await ApiRequestService({
                    method: 'post', url: '/getAllSubCategories',
                    data: { subject_id: group.subject_id, topic_id: group.topic_id, category_id: group.category_id },
                });

                const subcategories = subRes?.data?.data?.subCategory_names || [];
                const selectedIds = new Set(group.items.map(i => String(i.subcategory_id)));

                return subcategories
                    .filter(sc => selectedIds.has(String(sc.subcategory_id)))
                    .map(sc => ({
                        key: group.items.find(i => String(i.subcategory_id) === String(sc.subcategory_id)).key,
                        name: sc.subcategory_name,
                    }));
            })
        ).then(results => setSubcategoryNames(results.flat()));
    }, [keysStr, subcategoryMetadata]);

    const handleCheckboxChange = (key) => {
        setCheckedKeys(prev => {
            const next = new Set(prev);
            if (key === EXPECT_ALL_KEY) {
                // If "I expect..." is being toggled
                if (next.has(EXPECT_ALL_KEY)) {
                    next.delete(EXPECT_ALL_KEY);
                } else {
                    // Check "I expect..." and uncheck everything else
                    next.clear();
                    next.add(EXPECT_ALL_KEY);
                }
            } else {
                // A regular option is being toggled
                // Always uncheck "I expect..." when any other option is clicked
                next.delete(EXPECT_ALL_KEY);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
            }
            return next;
        });
    };

    if (subcategoryNames.length === 0) return null;

    return (
        <Grid>
            {subcategoryNames.map((sc, index) => (
                <Cell
                    key={sc.key}
                    className="w-full h-full"
                    size={11}
                >
                    <div className="sf-ans-btn d-flex justify-content-center align-items-center">
                        <input
                            type="checkbox"
                            checked={checkedKeys.has(sc.key)}
                            onChange={() => handleCheckboxChange(sc.key)}
                            style={{ width: 18, height: 18, marginRight: 8, accentColor: '#bb0000' }}
                            id={'exclude-ao-checkbox-preview-' + index}
                        />
                        <label className='p-0 m-0 w-100' htmlFor={'exclude-ao-checkbox-preview-' + index}>
                            {sc.name}
                        </label>
                    </div>
                </Cell>
            ))}
            <Cell
                key={EXPECT_ALL_KEY}
                className="w-full h-full"
                size={11}
            >
                <div className="sf-ans-btn d-flex justify-content-center align-items-center">
                    <input
                        type="checkbox"
                        checked={checkedKeys.has(EXPECT_ALL_KEY)}
                        onChange={() => handleCheckboxChange(EXPECT_ALL_KEY)}
                        style={{ width: 18, height: 18, marginRight: 8, accentColor: '#bb0000' }}
                        id={'exclude-ao-checkbox-preview-expect-all'}
                    />
                    <label className='p-0 m-0 w-100' htmlFor={'exclude-ao-checkbox-preview-expect-all'}>
                        I expect my students to be familiar with all these contexts
                    </label>
                </div>
            </Cell>
        </Grid>
    );
};

export default ExcludeAOAnswerOptions;
